<template>
  <b-overlay :show="loading" rounded="sm">
    <b-card
      :bg-variant="
        antiFraud.status === IntegrationStatus.INACTIVE ? 'light' : ''
      "
    >
      <template #header>
        <div class="d-flex align-items-center justify-content-between py-2">
          <span>
            {{ antiFraud.name }}
          </span>
          <b-badge
            v-if="antiFraud.status === IntegrationStatus.INACTIVE"
            variant="warning"
            class="ml-2"
          >
            <i class="far fa-clock mr-1"></i> Em breve
          </b-badge>
          <b-badge
            v-else-if="!antiFraud.enabledIntegration"
            variant="secondary"
            class="ml-2"
          >
            <i class="fas fa-toggle-off mr-1"></i> Desabilitado
          </b-badge>
          <b-badge
            v-else-if="!!antiFraud.enabledIntegration"
            :variant="getStatusColor(antiFraud.enabledIntegration.status)"
            class="ml-2"
          >
            <i
              :class="`${getStatusIcon(
                antiFraud.enabledIntegration.status
              )} mr-1`"
            ></i>
            {{ getStatusText(antiFraud.enabledIntegration.status) }}
          </b-badge>
        </div>
      </template>

      <b-card-body class="pa-0 p-0">
        <b-card-sub-title class="d-flex flex-column mb-2">
          <img :src="antiFraud.logo" height="40px" class="mx-auto mb-4" />
          <span>
            <i class="fas fa-info-circle mr-1"></i> Sobre a integração
          </span>
        </b-card-sub-title>
        <b-card-text v-if="antiFraud.status === IntegrationStatus.INACTIVE">
          <small> Integração ainda não disponível para ser habilitada. </small>
        </b-card-text>
        <b-card-text v-else-if="!antiFraud.enabledIntegration">
          <small>
            Integração disponível para ser habilitada. Clique no botão abaixo
            para começar o processo de autenticação.
          </small>

          <b-button
            variant="success"
            size="sm"
            class="mt-3 ml-auto d-block"
            @click="$emit('enable')"
          >
            <i class="fas fa-check mr-1"></i> Habilitar
          </b-button>
        </b-card-text>
        <b-card-text
          v-else-if="
            antiFraud.enabledIntegration.status ===
            EnabledIntegrationStatus.INACTIVE
          "
        >
          <small> Integração inativa. </small>

          <b-button
            variant="success"
            size="sm"
            class="mt-3 ml-auto d-block"
            @click="$emit('activate')"
          >
            <i class="fas fa-check mr-1"></i> Ativar
          </b-button>
        </b-card-text>
        <b-card-text
          v-else-if="
            antiFraud.enabledIntegration.status ===
            EnabledIntegrationStatus.ACTIVE
          "
        >
          <small>
            Integração ativa.
            <div class="d-flex flex-column">
              <span>
                <b>Atualizada em:</b>
                {{ antiFraud.enabledIntegration.updatedAt }}
              </span>
              <span>
                <b>Autorizada em:</b>
                {{ antiFraud.enabledIntegration.authorizedAt }}
              </span>
            </div>
          </small>

          <b-button
            variant="danger"
            size="sm"
            class="mt-3 ml-auto d-block"
            @click="$emit('disable')"
          >
            <i class="fas fa-ban mr-1"></i> Desativar
          </b-button>
        </b-card-text>
        <b-card-text
          v-else-if="
            antiFraud.enabledIntegration.status ===
            EnabledIntegrationStatus.DISABLED
          "
        >
          <small>
            Integração desabilitada.
            <div class="d-flex flex-column">
              <span>
                <b>Atualizada em:</b>
                {{ antiFraud.enabledIntegration.updatedAt }}
              </span>
            </div>
          </small>

          <b-button
            variant="success"
            size="sm"
            class="mt-3 ml-auto d-block"
            @click="$emit('activate')"
          >
            <i class="fas fa-check mr-1"></i> Ativar novamente
          </b-button>
        </b-card-text>
        <b-card-text
          v-else-if="
            antiFraud.enabledIntegration.status ===
            EnabledIntegrationStatus.ERROR
          "
        >
          <small>
            Ocorreu um erro ao realizar a integração. Por favor, contate um de
            nossos atendentes.
            <div class="d-flex flex-column">
              <span>
                <b>Atualizada em:</b>
                {{ antiFraud.enabledIntegration.updatedAt }}
              </span>
            </div>
          </small>
        </b-card-text>
        <b-card-text
          v-else-if="
            antiFraud.enabledIntegration.status ===
            EnabledIntegrationStatus.WAITING_AUTHORIZATION
          "
        >
          <small>
            Integração aguardando autorização/autenticação do usuário.

            <div class="d-flex flex-column mt-2">
              <span>
                <b>Atualizada em:</b>
                {{ antiFraud.enabledIntegration.updatedAt }}
              </span>
            </div>
          </small>

          <b-button
            v-if="!authorizationClicked"
            variant="success"
            size="sm"
            class="mt-3 ml-auto d-block"
            @click="
              () => {
                authorizationClicked = true;
                $emit('authorize');
              }
            "
          >
            <i class="fas fa-unlock mr-1"></i> Autorizar
          </b-button>
          <b-button
            v-else
            variant="info"
            size="sm"
            class="mt-3 ml-auto d-block"
            @click="$emit('refresh')"
          >
            <i class="fas fa-clipboard-check mr-1"></i> Já autorizei
          </b-button>
        </b-card-text>
      </b-card-body>
    </b-card>
  </b-overlay>
</template>

<script>
import IntegrationStatus from 'Enums/Integration/IntegrationStatus';
import EnabledIntegrationStatus, {
  getText as getStatusText,
} from 'Enums/Integration/EnabledIntegrationStatus';

export default {
  name: 'AntiFraudCard',
  emits: ['authorize', 'refresh', 'activate', 'disable', 'enable'],
  props: {
    antiFraud: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      IntegrationStatus,
      EnabledIntegrationStatus,
      authorizationClicked: false,
    };
  },
  methods: {
    getStatusText,
    getStatusColor(status) {
      const colors = {
        [EnabledIntegrationStatus.INACTIVE]: 'secondary',
        [EnabledIntegrationStatus.ACTIVE]: 'success',
        [EnabledIntegrationStatus.DISABLED]: 'secondary',
        [EnabledIntegrationStatus.WAITING_USER_ACTION]: 'warning',
        [EnabledIntegrationStatus.ERROR]: 'danger',
        [EnabledIntegrationStatus.WAITING_AUTHORIZATION]: 'info',
      };

      return colors[status];
    },
    getStatusIcon(status) {
      const icons = {
        [EnabledIntegrationStatus.INACTIVE]: 'fas fa-toggle-off',
        [EnabledIntegrationStatus.ACTIVE]: 'far fa-check-circle',
        [EnabledIntegrationStatus.DISABLED]: 'fas fa-toggle-off',
        [EnabledIntegrationStatus.WAITING_USER_ACTION]: 'far fa-clock',
        [EnabledIntegrationStatus.ERROR]: 'fas fa-exclamation-triangle',
        [EnabledIntegrationStatus.WAITING_AUTHORIZATION]: 'fas fa-user-lock',
      };

      return icons[status];
    },
  },
};
</script>
